import { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, Timer, Check, X, RotateCcw, Home, BookMarked } from "lucide-react";
import { toast } from "sonner";
import api, { apiErr } from "../../lib/api";
import { useTheme } from "../../context/ThemeContext";
import { useAuth } from "../../context/AuthContext";
import { ThemedShell, ThemedCard } from "../../components/child/ThemedShell";
import { Button } from "../../components/ui/button";
import { sfx } from "../../lib/sound";
import { CountUp } from "../../components/common/CountUp";

const SECONDS = 60;

export default function FlagQuiz() {
  const navigate = useNavigate();
  const location = useLocation();
  const wk = !!location.state?.wk;
  const { theme, terms } = useTheme();
  const { refresh } = useAuth();

  const [phase, setPhase] = useState("loading"); // loading | play | done
  const [qs, setQs] = useState([]);
  const [idx, setIdx] = useState(0);
  const [score, setScore] = useState(0);
  const [answered, setAnswered] = useState(0);
  const [left, setLeft] = useState(SECONDS);
  const [flash, setFlash] = useState(null);
  const [picked, setPicked] = useState(null);
  const [result, setResult] = useState(null);
  const tick = useRef(null);
  const lock = useRef(false);
  const finished = useRef(false);
  const seenRef = useRef([]);

  const loadQuiz = () => {
    finished.current = false; seenRef.current = [];
    setResult(null); setIdx(0); setScore(0); setAnswered(0); setLeft(SECONDS); setPicked(null);
    api.get(`/child/world/flagquiz?wk=${wk}`).then(({ data }) => {
      setQs(data.questions); setPhase("play");
    }).catch((err) => toast.error(apiErr(err.response?.data?.detail)));
  };
  useEffect(loadQuiz, []); // eslint-disable-line

  useEffect(() => {
    if (phase !== "play") return;
    tick.current = setInterval(() => setLeft((s) => s - 1), 1000);
    return () => clearInterval(tick.current);
  }, [phase]);

  useEffect(() => {
    if (phase === "play" && left <= 0) { clearInterval(tick.current); finish(); }
    // eslint-disable-next-line
  }, [left, phase]);

  const finish = async (fScore, fAns) => {
    if (finished.current) return;
    finished.current = true;
    clearInterval(tick.current);
    const correct = fScore != null ? fScore : score;
    const tot = fAns != null ? fAns : answered;
    setPhase("done");
    try {
      const { data } = await api.post("/child/world/flagquiz/finish", {
        correct, total: tot, duration: SECONDS, seen: seenRef.current, wk,
      });
      setResult(data);
      const pct = tot ? Math.round((correct / tot) * 100) : 0;
      if (pct >= 80) sfx.win(); else sfx.coin();
      refresh();
    } catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
  };

  const choose = (j) => {
    if (lock.current || finished.current) return;
    const q = qs[idx];
    const ok = j === q.answer;
    lock.current = true;
    setPicked(j);
    setFlash(ok ? "ok" : "no");
    if (ok) { sfx.correct(); seenRef.current.push(q.code); } else sfx.wrong();
    const newScore = score + (ok ? 1 : 0);
    const newAns = answered + 1;
    setScore(newScore); setAnswered(newAns);
    setTimeout(() => {
      setFlash(null); setPicked(null); lock.current = false;
      setIdx((i) => {
        if (i + 1 >= qs.length || left <= 0) { setTimeout(() => finish(newScore, newAns), 0); return i; }
        return i + 1;
      });
    }, 520);
  };

  const muted = { color: "var(--t-muted)" };

  if (phase === "loading") {
    return <ThemedShell><div className="min-h-screen flex items-center justify-center"><div className="w-12 h-12 rounded-full border-4 animate-spin" style={{ borderColor: "var(--t-surface2)", borderTopColor: "var(--t-accent)" }} /></div></ThemedShell>;
  }

  if (phase === "done") {
    const tot = result?.total ?? answered;
    const sc = result?.score ?? score;
    const pct = tot ? Math.round((sc / tot) * 100) : 0;
    const great = pct >= 80;
    return (
      <ThemedShell>
        <div className="max-w-2xl mx-auto px-5 pt-12 pb-16">
          <motion.div initial={{ scale: 0.85, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}>
            <ThemedCard className="p-8 text-center">
              <motion.div className="text-6xl mb-3" animate={great ? { scale: [0, 1.3, 1], rotate: [-20, 10, 0] } : { scale: 1 }} transition={{ duration: 0.8 }}>{great ? (wk ? "🏆" : "🎌") : theme.mascot}</motion.div>
              <h1 className="font-heading text-3xl font-black themed-heading">{great ? "Vlaggenkenner!" : sc > 0 ? "Goed gedaan!" : "Blijf oefenen!"}</h1>
              <p className="mt-2 font-semibold" style={muted}>Je had <b>{sc}</b> van de <b>{tot}</b> vlaggen goed</p>
              <div className="my-6 font-heading text-6xl font-black themed-heading" data-testid="flag-score">{sc}</div>
              <div className="flex justify-center gap-3 flex-wrap">
                <div className="flex items-center gap-2 px-4 py-2 rounded-2xl" style={{ background: "var(--t-surface2)" }} data-testid="flag-xp"><span className="font-heading font-extrabold">+<CountUp value={result?.xp_earned || 0} delay={250} /> {terms.xp}</span></div>
                <div className="flex items-center gap-2 px-4 py-2 rounded-2xl" style={{ background: "var(--t-surface2)" }} data-testid="flag-coins"><span className="text-lg">{terms.coinIcon}</span><span className="font-heading font-extrabold">+<CountUp value={result?.coins_earned || 0} delay={400} /> {terms.coins}</span></div>
              </div>
              {result?.new_badges?.length > 0 && (
                <div className="mt-6 p-4 rounded-2xl" style={{ background: "var(--t-surface2)" }} data-testid="flag-badges">
                  <p className="font-heading font-bold">Nieuwe {terms.badges.toLowerCase()}! 🎉</p>
                  <div className="flex justify-center gap-2 mt-3 flex-wrap">
                    {result.new_badges.map((b) => <span key={b.id} className="px-3 py-1.5 rounded-full font-bold text-sm" style={{ background: "var(--t-accent)", color: "var(--t-on-accent)" }}>{b.title}</span>)}
                  </div>
                </div>
              )}
              <div className="flex flex-col sm:flex-row gap-3 mt-8">
                <Button onClick={() => { setPhase("loading"); loadQuiz(); }} className="flex-1 h-12 rounded-xl font-bold border-0" style={{ background: "var(--t-primary)", color: "var(--t-on-primary)" }} data-testid="flag-again-btn"><RotateCcw className="w-4 h-4 mr-1.5" /> Nog een keer</Button>
                <Button onClick={() => navigate("/leerling/paspoort")} className="flex-1 h-12 rounded-xl font-bold border-2 bg-transparent" style={{ borderColor: "var(--t-border)", color: "var(--t-text)" }} data-testid="flag-passport-btn"><BookMarked className="w-4 h-4 mr-1.5" /> Paspoort</Button>
                <Button onClick={() => navigate("/leerling/wereldreis")} className="flex-1 h-12 rounded-xl font-bold border-2 bg-transparent" style={{ borderColor: "var(--t-border)", color: "var(--t-text)" }} data-testid="flag-home-btn"><Home className="w-4 h-4 mr-1.5" /> Wereldreis</Button>
              </div>
            </ThemedCard>
          </motion.div>
        </div>
      </ThemedShell>
    );
  }

  // PLAY
  const q = qs[idx] || { options: [] };
  const pctLeft = Math.max(0, (left / SECONDS) * 100);
  return (
    <ThemedShell>
      <div className="max-w-2xl mx-auto px-5 pt-6 pb-16">
        <div className="flex items-center justify-between mb-3">
          <Button variant="ghost" onClick={() => navigate("/leerling/wereldreis")} className="rounded-full font-bold px-3" style={{ color: "var(--t-text)" }} data-testid="flag-back-btn"><ArrowLeft className="w-5 h-5" /></Button>
          <span className="flex items-center gap-1.5 px-3 py-1.5 rounded-full font-heading font-extrabold" style={{ background: "var(--t-surface2)", color: left <= 10 ? "var(--t-primary)" : "var(--t-text)" }} data-testid="flag-timer"><Timer className="w-4 h-4" /> {left}s</span>
          <span className="font-heading font-extrabold" data-testid="flag-live-score">{wk ? "🏆" : "🎌"} {score}</span>
        </div>
        <div className="h-2.5 rounded-full overflow-hidden mb-8" style={{ background: "var(--t-surface2)" }}>
          <motion.div className="h-full rounded-full" style={{ background: "var(--t-accent)" }} animate={{ width: `${pctLeft}%` }} transition={{ ease: "linear", duration: 0.9 }} />
        </div>

        <AnimatePresence mode="wait">
          <motion.div key={idx} initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -18 }} transition={{ duration: 0.18 }}>
            <ThemedCard className="p-7 text-center" style={flash === "ok" ? { borderColor: "var(--t-success)" } : flash === "no" ? { borderColor: "var(--t-primary)" } : {}}>
              <p className="font-bold mb-4" style={muted}>Van welk land is deze vlag?</p>
              <div className="mx-auto mb-7 rounded-2xl overflow-hidden border-2 shadow-lg" style={{ borderColor: "var(--t-border)", width: 200, height: 134 }}>
                <img src={`https://flagcdn.com/w320/${q.code}.png`} alt="vlag" className="w-full h-full object-cover" data-testid="flag-image" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                {q.options.map((o, j) => {
                  const isAns = picked != null && j === q.answer;
                  const isWrongPick = picked === j && j !== q.answer;
                  return (
                    <button key={j} onClick={() => choose(j)} data-testid={`flag-opt-${j}`}
                      className="min-h-14 py-3 px-2 rounded-2xl font-heading font-extrabold text-base sm:text-lg border-2 transition-all active:scale-95"
                      style={isAns ? { background: "var(--t-success)", color: "#fff", borderColor: "var(--t-success)" }
                        : isWrongPick ? { background: "var(--t-primary)", color: "#fff", borderColor: "var(--t-primary)" }
                        : { background: "var(--t-surface2)", color: "var(--t-text)", borderColor: "var(--t-border)" }}>
                      {o}
                    </button>
                  );
                })}
              </div>
            </ThemedCard>
          </motion.div>
        </AnimatePresence>

        {flash && (
          <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} className="fixed inset-x-0 top-24 flex justify-center pointer-events-none z-50">
            <div className="w-16 h-16 rounded-full flex items-center justify-center" style={{ background: flash === "ok" ? "var(--t-success)" : "var(--t-primary)", color: "#fff" }}>
              {flash === "ok" ? <Check className="w-8 h-8" strokeWidth={3} /> : <X className="w-8 h-8" strokeWidth={3} />}
            </div>
          </motion.div>
        )}
      </div>
    </ThemedShell>
  );
}
